import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link2, Link2Off, RefreshCw, CheckCircle2, AlertTriangle, MapPin, Loader2, Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";

export default function GoogleConnection() {
  const [data, setData] = useState({ configured: false, connected: false, accounts: [] });
  const [loading, setLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState({});
  const [accountLocations, setAccountLocations] = useState({});
  const [selectedLocations, setSelectedLocations] = useState({});
  const [manualAccountLoc, setManualAccountLoc] = useState(null);
  const [manualData, setManualData] = useState({ accountId: '', locationId: '', locationName: '' });
  const [params] = useSearchParams();

  const load = async () => {
    try {
      const { data: res } = await api.get("/google/accounts");
      setData(res);
      // Automatically fetch locations if connected but no location is selected? Or just wait for user.
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => {
    load().then(() => {});
    if (params.get("connected")) toast.success("Google account connected");
    if (params.get("error")) toast.error("Google connection failed: " + params.get("error"));
  }, []); // eslint-disable-line

  const connect = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/google/auth-url");
      if (!res.configured) {
        toast.info(res.message);
      } else {
        window.location.href = res.url;
      }
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const disconnect = async (googleAccountId) => {
    if (!confirm("Are you sure you want to disconnect this account?")) return;
    try {
      await api.post("/google/disconnect", { googleAccountId });
      toast.success("Disconnected");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const loadLocations = async (googleAccountId) => {
    setLocationsLoading((prev) => ({ ...prev, [googleAccountId]: true }));
    try {
      const { data: res } = await api.get(`/google/locations?googleAccountId=${googleAccountId}`);
      if (res.message) {
        if (res.locations?.length) toast.success(res.message);
        else toast.warning(res.message);
      }
      setAccountLocations((prev) => ({ ...prev, [googleAccountId]: res.locations || [] }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLocationsLoading((prev) => ({ ...prev, [googleAccountId]: false }));
    }
  };

  const toggleLocation = (googleAccountId, loc) => {
    setSelectedLocations((prev) => {
      const selected = prev[googleAccountId] || [];
      const isSelected = selected.some((s) => s.locationId === loc.locationId);
      if (isSelected) {
        return { ...prev, [googleAccountId]: selected.filter((s) => s.locationId !== loc.locationId) };
      } else {
        return { ...prev, [googleAccountId]: [...selected, loc] };
      }
    });
  };

  const saveLocations = async (googleAccountId) => {
    const locations = selectedLocations[googleAccountId] || [];
    if (!locations.length) return toast.info("Please select at least one location.");
    try {
      const { data: res } = await api.post("/google/select-location", { googleAccountId, locations });
      toast.success(res.message || "Locations saved.");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleManualAdd = async () => {
    if (!manualData.accountId || !manualData.locationId) return toast.error("Account ID and Location ID are required");
    try {
      const { data: res } = await api.post("/google/locations/manual", { 
        googleAccountId: manualAccountLoc, 
        ...manualData 
      });
      toast.success(res.message || "Location manually added.");
      setManualAccountLoc(null);
      setManualData({ accountId: '', locationId: '', locationName: '' });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!data) return <div className="space-y-6"><PageHeader title="Google Connection" /></div>;

  return (
    <div className="space-y-6">
      <PageHeader title="Google Connection" subtitle="Connect multiple Google Business Profile accounts securely" icon={Link2} />

      {!data.configured && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Google OAuth credentials are not configured yet. Open Super Admin → Settings → API Connections, add Google Client ID, Client Secret and Redirect URI, then save settings.
        </div>
      )}

      {data.accounts.map((account) => (
        <div key={account._id} className="card-surface p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${account.status === 'connected' ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  {account.googleEmail || 'Connected Google Account'}
                </h3>
                <div className="mt-1 flex items-center gap-2">
                  <StatusBadge status={account.status} />
                  <span className="text-xs text-slate-500">ID: {account._id}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => disconnect(account._id)} variant="outline" className="rounded-xl text-red-600 hover:bg-red-50">
                <Link2Off className="mr-1 h-4 w-4" /> Disconnect
              </Button>
            </div>
          </div>
          
          {account.savedLocations?.length > 0 && (
            <div className="mt-6 p-4 rounded-xl border border-green-200 bg-green-50">
              <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-green-800 mb-3">
                <CheckCircle2 className="h-4 w-4" /> Connected Locations
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {account.savedLocations.map(loc => (
                  <div key={loc._id} className="p-3 bg-white border border-green-100 rounded-lg shadow-sm">
                    <p className="font-semibold text-green-900 text-sm">{loc.locationName || loc.locationId}</p>
                    <p className="text-xs text-green-700">Account ID: {loc.accountId}</p>
                    <p className="text-xs text-green-700">Location ID: {loc.locationId}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 border-t border-slate-100 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800">
                <MapPin className="h-5 w-5 text-brand-600" /> Manage Locations
              </h4>
              <div className="flex items-center gap-2">
                <Button onClick={() => setManualAccountLoc(account._id)} variant="ghost" className="rounded-xl text-slate-500 hover:text-brand-600 hover:bg-brand-50" size="sm">
                  Add Manually
                </Button>
                <Button onClick={() => loadLocations(account._id)} variant="outline" className="rounded-xl" disabled={locationsLoading[account._id]}>
                  {locationsLoading[account._id] ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
                  Fetch Locations
                </Button>
                <Button onClick={() => saveLocations(account._id)} className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" disabled={!(selectedLocations[account._id]?.length > 0)}>
                  <Building2 className="mr-1 h-4 w-4" /> Save Selection
                </Button>
              </div>
            </div>

            {manualAccountLoc === account._id && (
              <div className="mb-4 rounded-xl border border-brand-200 bg-brand-50 p-4">
                <h5 className="font-semibold text-brand-800 mb-2 text-sm">Add Location Manually (Bypass Quotas)</h5>
                <p className="text-xs text-brand-600 mb-4">If Google API is blocking you with a 429 quota error, you can use third-party tools (like Pleper Chrome Extension) to find your GMB Account ID and Location ID and paste them here to instantly connect.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input type="text" placeholder="Account ID (e.g. 1118123...)" className="rounded-lg border-slate-200 text-sm" value={manualData.accountId} onChange={e => setManualData({...manualData, accountId: e.target.value})} />
                  <input type="text" placeholder="Location ID (e.g. 158522...)" className="rounded-lg border-slate-200 text-sm" value={manualData.locationId} onChange={e => setManualData({...manualData, locationId: e.target.value})} />
                  <input type="text" placeholder="Location Name (Optional)" className="rounded-lg border-slate-200 text-sm" value={manualData.locationName} onChange={e => setManualData({...manualData, locationName: e.target.value})} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setManualAccountLoc(null)}>Cancel</Button>
                  <Button size="sm" onClick={handleManualAdd} className="bg-brand-600 hover:bg-brand-700 text-white">Save Manual Location</Button>
                </div>
              </div>
            )}

            {accountLocations[account._id] && accountLocations[account._id].length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {accountLocations[account._id].map((loc) => {
                  const isSelected = (selectedLocations[account._id] || []).some(s => s.locationId === loc.locationId);
                  return (
                    <div key={loc.locationId} onClick={() => toggleLocation(account._id, loc)} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${isSelected ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:border-brand-300'}`}>
                      <input type="checkbox" checked={isSelected} readOnly className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                      <div>
                        <p className="font-semibold text-slate-800">{loc.locationName || loc.locationId}</p>
                        {loc.locationAddress && <p className="text-sm text-slate-500">{loc.locationAddress}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
                Click "Fetch Locations" to retrieve the Google Business Profiles for this account.
              </div>
            )}
          </div>
        </div>
      ))}

      <div className="flex justify-center mt-6">
        <Button onClick={connect} disabled={loading} className="rounded-xl bg-brand-700 px-6 py-6 text-white hover:bg-brand-800 shadow-sm transition-all hover:shadow-md">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="mr-2 h-5 w-5" /> Connect Another Google Account</>}
        </Button>
      </div>

    </div>
  );
}
